import type { RabbitPresetData, SensorObstacle, Vec2 } from "../types";
import { add, cloneVec, length, scale, sub } from "./math";
import { TrackRuntime } from "./track";

export interface RabbitState {
  id: string;
  position: Vec2;
  previousPosition: Vec2;
  heading: number;
  baseProgress: number;
  currentProgress: number;
  forwardSpeed: number;
  side: number;
  phase: number;
  crossingPeriod: number;
  crossingWidth: number;
}

export interface RabbitDecal {
  position: Vec2;
  heading: number;
  rabbitId: string;
  elapsedSeconds: number;
}

export class RabbitWorld {
  readonly track: TrackRuntime;
  readonly rabbits: RabbitState[];
  readonly decals: RabbitDecal[] = [];
  elapsed = 0;

  constructor(track: TrackRuntime, preset: RabbitPresetData) {
    this.track = track;
    this.rabbits = preset.rabbits.map((rabbit) => ({
      id: rabbit.id,
      position: cloneVec(rabbit.position),
      previousPosition: cloneVec(rabbit.position),
      heading: rabbit.heading,
      baseProgress: rabbit.base_progress,
      currentProgress: rabbit.current_progress,
      forwardSpeed: rabbit.forward_speed,
      side: rabbit.side,
      phase: rabbit.phase,
      crossingPeriod: rabbit.crossing_period,
      crossingWidth: rabbit.crossing_width,
    }));
  }

  private positionFor(rabbit: RabbitState, elapsed: number): Vec2 {
    const { point, heading } = this.track.pointAt(rabbit.currentProgress);
    const normal: Vec2 = [-Math.sin(heading), Math.cos(heading)];
    const outside = this.track.project(point).allowedHalfWidth + 24;
    const cycle = ((elapsed / rabbit.crossingPeriod + rabbit.phase / (2 * Math.PI)) % 1 + 1) % 1;
    const lateral = rabbit.side * outside * Math.tanh(Math.cos(2 * Math.PI * cycle) / (0.42 + rabbit.crossingWidth * 0.5));
    return add(point, scale(normal, lateral));
  }

  step(dt: number): void {
    this.elapsed += dt;
    for (const rabbit of this.rabbits) {
      rabbit.previousPosition = cloneVec(rabbit.position);
      const stopFactor = 0.15 + 0.85 * (0.5 + 0.5 * Math.sin(this.elapsed * 0.31 + rabbit.phase));
      rabbit.currentProgress += rabbit.forwardSpeed * stopFactor * dt;
      const target = this.positionFor(rabbit, this.elapsed);
      let delta = sub(target, rabbit.previousPosition);
      const distance = length(delta);
      const maximumStep = 46 * dt;
      if (distance > maximumStep) delta = scale(delta, maximumStep / distance);
      rabbit.position = add(rabbit.previousPosition, delta);
      if (length(delta) > 1e-9) rabbit.heading = Math.atan2(delta[1], delta[0]);
    }
  }

  obstacles(): SensorObstacle[] {
    return this.rabbits.map((rabbit) => ({
      center: cloneVec(rabbit.position),
      radius: 7,
      hitType: "RABBIT",
      objectId: rabbit.id,
    }));
  }

  addDecal(rabbit: RabbitState): void {
    this.decals.push({
      position: cloneVec(rabbit.position),
      heading: rabbit.heading,
      rabbitId: rabbit.id,
      elapsedSeconds: this.elapsed,
    });
  }
}
